import { useState, useEffect, useCallback, useMemo } from 'react';
import { userPrefsApi, type Bookmark, type BookmarkFolder } from '@/services/userPrefsApi';

export function useBookmarks() {
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async (folder?: string) => {
    setLoading(true);
    try {
      const items = await userPrefsApi.listBookmarks(folder);
      setBookmarks(items);
    } catch {
      // Non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const add = useCallback(async (bookmark: {
    docId: string;
    title: string;
    repoFullName: string;
    path: string;
    docType: string;
    folderId?: string;
  }) => {
    const created = await userPrefsApi.createBookmark(bookmark);
    setBookmarks((prev) => [created, ...prev]);
    return created;
  }, []);

  const remove = useCallback(async (docIdHash: string) => {
    setBookmarks((prev) => prev.filter((b) => b.docIdHash !== docIdHash));
    try {
      await userPrefsApi.deleteBookmark(docIdHash);
    } catch {
      fetch();
    }
  }, [fetch]);

  const update = useCallback(async (docIdHash: string, updates: { folderId?: string | null; note?: string | null }) => {
    const updated = await userPrefsApi.updateBookmark(docIdHash, updates);
    setBookmarks((prev) => prev.map((b) => b.docIdHash === docIdHash ? updated : b));
    return updated;
  }, []);

  /** Set of bookmarked docId hashes for O(1) lookup. */
  const bookmarkedSet = useMemo(
    () => new Set(bookmarks.map((b) => b.docIdHash)),
    [bookmarks],
  );

  return { bookmarks, loading, add, remove, update, refresh: fetch, bookmarkedSet };
}

export function useBookmarkFolders() {
  const [folders, setFolders] = useState<BookmarkFolder[]>([]);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const items = await userPrefsApi.listBookmarkFolders();
      setFolders(items);
    } catch {
      // Non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const create = useCallback(async (name: string, color?: string) => {
    const folder = await userPrefsApi.createBookmarkFolder(name, color);
    setFolders((prev) => [...prev, folder]);
    return folder;
  }, []);

  const update = useCallback(async (folderId: string, updates: { name?: string; color?: string; sortOrder?: number }) => {
    const updated = await userPrefsApi.updateBookmarkFolder(folderId, updates);
    setFolders((prev) => prev.map((f) => f.folderId === folderId ? updated : f));
    return updated;
  }, []);

  const remove = useCallback(async (folderId: string) => {
    setFolders((prev) => prev.filter((f) => f.folderId !== folderId));
    try {
      await userPrefsApi.deleteBookmarkFolder(folderId);
    } catch {
      fetch();
    }
  }, [fetch]);

  return { folders, loading, create, update, remove, refresh: fetch };
}
